/**
 * The single definition of the percentage the Item-Wise Sales Report publishes.
 *
 * WHY THIS MODULE EXISTS
 * ----------------------
 * Two screens render rows from the SAME endpoint — GET /stockrepo/item-wise-sales — under
 * the same title, and they disagreed about what "the percentage" meant:
 *
 *   Stock Report -> Item-Wise Sales Report  printed `margin`                  (gross margin)
 *   Dashboard carousel slide                printed `contributionPercentage`  (share of revenue)
 *
 * For business 173 over Aug-2026 that put Chicken Nuggets (Weekend) at 85.0% on the Stock
 * Report and 11.9% on the Dashboard. Both figures were individually correct; showing them
 * under one report name was not. The Stock Report's `margin` is the report's figure, so the
 * Dashboard now reads it through here.
 *
 * `contributionPercentage` is still returned by /stockrepo/item-wise-sales and by the
 * legacy /__legacy-item-wise-sales route — nothing on the API side changed. It is simply
 * not what this report's percentage means, so no screen renders it as one.
 *
 * Every caller must go through getItemMarginDisplay(). Formatting the field by hand at a
 * call site is how the two screens drifted apart in the first place.
 */

/**
 * Tooltip for a margin whose COGS was partly priced from an old purchase price.
 * Moved here verbatim from stockrepo.js so the Dashboard explains "~" identically.
 */
export const formatEstimatedCogsTooltip = (item) => {
    const lines = Array.isArray(item?.estimatedIngredients) ? item.estimatedIngredients : [];
    if (lines.length === 0) {
        return 'Estimated: part of this cost was priced from an old purchase price because the ingredient had no stock and no confirmed closing-stock price.';
    }
    const described = lines.slice(0, 3).map((l) => {
        const when = l.pricedOn
            ? new Date(l.pricedOn).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
            : 'date unknown';
        return `${l.name} (last purchased ${when})`;
    });
    const more = lines.length > described.length ? ` +${lines.length - described.length} more` : '';
    return `Estimated from last purchase price — no stock and no confirmed closing-stock price for: ${described.join(', ')}${more}`;
};

const COLOR_ESTIMATED = '#a16207';
const COLOR_NEGATIVE = '#dc2626';
const COLOR_POSITIVE = '#16a34a';
const COLOR_UNAVAILABLE = '#666';

/**
 * Resolve one item row into the text/colour/tooltip the Item-Wise Sales Report shows.
 *
 * @param {object} item A row from /stockrepo/item-wise-sales.
 * @returns {{available: boolean, value: number|null, text: string, color: string, title: string|undefined, estimated: boolean}}
 */
export const getItemMarginDisplay = (item) => {
    const margin = item?.margin;

    // COGS could not be established (negative stock with no stock-in and no confirmed
    // closing-stock price, or no costable recipe). A dash, never a 0% — "unknown" has to
    // stay visibly different from "zero profit".
    if (margin == null || !Number.isFinite(Number(margin))) {
        return {
            available: false,
            value: null,
            text: '-',
            color: COLOR_UNAVAILABLE,
            title: item?.cogsUnavailableReason === 'negative_stock'
                ? 'Gross profit unavailable: an ingredient went into negative stock and has no stock-in or confirmed closing-stock price'
                : 'Gross profit unavailable: this item has no costable recipe',
            estimated: false
        };
    }

    const value = Number(margin);
    const estimated = Boolean(item?.cogsEstimated);

    return {
        available: true,
        value,
        // An estimated margin is a real number, but part of the cost behind it came from an
        // old purchase price. "~" is the only thing separating it from a measured figure,
        // so it is never dropped.
        text: `${estimated ? '~' : ''}${value.toFixed(1)}%`,
        color: estimated ? COLOR_ESTIMATED : (value < 0 ? COLOR_NEGATIVE : COLOR_POSITIVE),
        title: estimated ? formatEstimatedCogsTooltip(item) : undefined,
        estimated
    };
};
